import React from 'react';
import { useNavigate } from 'react-router-dom';

const styles = {
  global: `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
      background: linear-gradient(135deg, #e8f0e9 0%, #d4e5d8 50%, #c5dde0 100%);
      min-height: 100vh;
      color: #3d4f4f;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }
    
    @keyframes pulse {
      0%, 100% { box-shadow: 0 8px 30px rgba(124, 152, 133, 0.4); }
      50% { box-shadow: 0 8px 50px rgba(124, 152, 133, 0.6); }
    }
    
    button:hover {
      transform: translateY(-2px);
    }
    
    button:active {
      transform: translateY(0);
    }
  `,

  // Header / Navigation
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    padding: '20px 40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 100,
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(124, 152, 133, 0.1)',
  },
  
  logo: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#3d4f4f',
  },
  
  headerButtons: {
    display: 'flex',
    gap: '12px',
  },
  
  signInButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#5a7070',
    background: 'transparent',
    border: '2px solid rgba(124, 152, 133, 0.3)',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  
  signUpButtonSmall: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },

  // Main container
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  },

  // Background decorations
  bgCircle1: {
    position: 'absolute',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'rgba(124, 152, 133, 0.12)',
    top: '-150px',
    right: '-100px',
    animation: 'float 8s ease-in-out infinite',
    pointerEvents: 'none',
  },
  
  bgCircle2: {
    position: 'absolute',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'rgba(147, 180, 186, 0.1)',
    bottom: '100px',
    left: '-150px',
    animation: 'float 10s ease-in-out infinite 1s',
    pointerEvents: 'none',
  },
  
  bgCircle3: {
    position: 'absolute',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'rgba(200, 180, 160, 0.08)',
    top: '40%',
    left: '10%',
    animation: 'float 6s ease-in-out infinite 0.5s',
    pointerEvents: 'none',
  },

  // Hero Section
  heroSection: {
    paddingTop: '160px',
    paddingBottom: '80px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
    zIndex: 1,
    animation: 'fadeIn 0.8s ease-out',
  },

  h1: {
    fontSize: 'clamp(40px, 7vw, 72px)',
    fontWeight: '700',
    color: '#3d4f4f',
    lineHeight: '1.1',
    marginBottom: '24px',
    maxWidth: '900px',
    padding: '0 20px',
  },

  h1Span: {
    color: '#7c9885',
    position: 'relative',
  },

  h2: {
    fontSize: 'clamp(18px, 2.5vw, 24px)',
    fontWeight: '400',
    color: '#5a7070',
    maxWidth: '700px',
    lineHeight: '1.6',
    marginBottom: '40px',
    padding: '0 20px',
  },

  // CTA Button
  ctaButton: {
    padding: '20px 48px',
    fontSize: '18px',
    fontWeight: '700',
    color: 'white',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    border: 'none',
    borderRadius: '50px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
    boxShadow: '0 8px 30px rgba(124, 152, 133, 0.4)',
    animation: 'pulse 2s ease-in-out infinite',
  },

  // How It Works Section
  howItWorksSection: {
    padding: '80px 40px',
    background: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(20px)',
    position: 'relative',
    zIndex: 1,
  },
  
  sectionTitle: {
    textAlign: 'center',
    fontSize: 'clamp(28px, 4vw, 40px)',
    fontWeight: '700',
    color: '#3d4f4f',
    marginBottom: '60px',
  },
  
  stepsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    maxWidth: '1200px',
    margin: '0 auto',
    flexWrap: 'wrap',
  },
  
  stepCard: {
    flex: '1',
    minWidth: '280px',
    maxWidth: '350px',
    background: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '24px',
    padding: '40px 32px',
    textAlign: 'center',
    boxShadow: '0 8px 32px rgba(61, 79, 79, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s ease',
  },
  
  stepNumber: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    color: 'white',
    fontSize: '24px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    boxShadow: '0 4px 16px rgba(124, 152, 133, 0.3)',
  },
  
  stepIcon: {
    fontSize: '48px',
    marginBottom: '20px',
  },
  
  stepTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '12px',
  },
  
  stepDescription: {
    fontSize: '16px',
    color: '#5a7070',
    lineHeight: '1.6',
  },

  // Bottom CTA Section
  bottomCtaSection: {
    padding: '80px 40px',
    textAlign: 'center',
    position: 'relative',
    zIndex: 1,
  },
  
  bottomCtaText: {
    fontSize: 'clamp(20px, 3vw, 28px)',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '32px',
  },

  // Footer
  footer: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b8080',
    fontSize: '14px',
    position: 'relative',
    zIndex: 1,
  },
};

function LandingPage() {
  const navigate = useNavigate();

  const handleSignUp = () => {
    navigate('/auth?mode=register');
  };

  const handleSignIn = () => {
    navigate('/auth?mode=login');
  };

  return (
    <>
      <style>{styles.global}</style>
      
      <div style={styles.container}>
        {/* Background decorations */}
        <div style={styles.bgCircle1}></div>
        <div style={styles.bgCircle2}></div>
        <div style={styles.bgCircle3}></div>

        {/* Header */}
        <header style={styles.header}>
          <div style={styles.logo}>Instavju</div>
          <div style={styles.headerButtons}>
            <button 
              style={styles.signInButton}
              onClick={handleSignIn}
            >
              Sign in
            </button>
            <button 
              style={styles.signUpButtonSmall}
              onClick={handleSignUp}
            >
              Sign up
            </button>
          </div>
        </header>

        {/* Hero Section */}
        <section style={styles.heroSection}>
          <h1 style={styles.h1}>
            Get interviews in an <span style={styles.h1Span}>instant!</span>
          </h1>
          
          <h2 style={styles.h2}>
            Instavju is a real-time interview-matching platform designed for humans.
          </h2>
          
          <button 
            style={styles.ctaButton}
            onClick={handleSignUp}
          >
            Sign up for free
          </button>
        </section>

        {/* How It Works Section */}
        <section style={styles.howItWorksSection}>
          <h2 style={styles.sectionTitle}>How it works</h2>
          
          <div style={styles.stepsContainer}>
            {/* Step 1 */}
            <div style={styles.stepCard}>
              <div style={styles.stepNumber}>1</div>
              <div style={styles.stepIcon}>👤</div>
              <h3 style={styles.stepTitle}>Create your profile</h3>
              <p style={styles.stepDescription}>
                Users create their profile, set their search parameters, and click "Get interview!"
              </p>
            </div>

            {/* Step 2 */}
            <div style={styles.stepCard}>
              <div style={styles.stepNumber}>2</div>
              <div style={styles.stepIcon}>⏱️</div>
              <h3 style={styles.stepTitle}>Review your match</h3>
              <p style={styles.stepDescription}>
                When users match, they get 60 seconds to look at the other's profile and see if they want to interview.
              </p>
            </div>

            {/* Step 3 */}
            <div style={styles.stepCard}>
              <div style={styles.stepNumber}>3</div>
              <div style={styles.stepIcon}>🎥</div>
              <h3 style={styles.stepTitle}>Start the interview</h3>
              <p style={styles.stepDescription}>
                If it's a match, the interview starts immediately. No scheduling, no waiting.
              </p>
            </div>
          </div>
        </section>

        {/* Bottom CTA Section */}
        <section style={styles.bottomCtaSection}>
          <p style={styles.bottomCtaText}>
            Ready to transform your hiring process?
          </p>
          <button 
            style={styles.ctaButton}
            onClick={handleSignUp}
          >
            Sign up now!
          </button>
        </section>

        {/* Footer */}
        <footer style={styles.footer}>
          <p>© 2025 Instavju. All rights reserved.</p>
        </footer>
      </div>
    </>
  );
}

export default LandingPage;